import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { listTurnos, listAperturasActivas, abrirTurno } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { todayISO, sumarDiasCalendario, formatDateTime } from '../../utils/dates';
import MoneyInput from '../common/MoneyInput';
import './styles/apertura.css';

// `turnoFijo`: turno elegido en la entrada a Caja (Día/Noche). Día completo no lo usa.
// `soloAnterior`: registrar una fecha anterior que quedó sin cuadre, aunque hoy ya tenga caja abierta o cerrada.
export default function AperturaTurno({ turnoFijo = '', soloAnterior = false }) {
  const { me, sucursales, sucursalId } = useSucursalActiva();
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [activas, setActivas] = useState([]);
  const [turnoId, setTurnoId] = useState('');
  const [efectivo, setEfectivo] = useState(''); // vacío: "0.00" es solo la sombra; vacío equivale a 0
  // Por defecto hoy; puede elegirse una fecha anterior si ese turno quedó sin cuadre.
  const hoy = todayISO();
  const maxFecha = soloAnterior ? sumarDiasCalendario(hoy, -1) : hoy;
  const [fecha, setFecha] = useState(maxFecha);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const visibles = useMemo(() => me.role === 'admin' ? sucursales : sucursales.filter(s => s.id === me.sucursalId), [sucursales, me.role, me.sucursalId]);

  useEffect(() => {
    let active = true;
    Promise.all([listTurnos(), listAperturasActivas()]).then(([t, a]) => {
      if (!active) return;
      setTurnos(t); setActivas(a); if (t.length) setTurnoId(t[0].id);
    }).catch(e => Swal.fire('Error', e.message, 'error')).finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  // Solo hay una caja abierta por sucursal: si existe, primero se cierra su cuadre.
  // Identidad de la caja: sucursal + turno (Día y Noche pueden estar abiertas a la vez; cada una se cierra por separado).
  const existente = !soloAnterior && activas.find(a => a.sucursalId === sucursalId && (!turnoFijo || a.turnoId === turnoFijo));
  // Jornada única: una apertura por día, siempre "Día completo" (no se pide turno).
  // Esquema de turnos de la sucursal: Día completo (jornada única) o Día y Noche (por turnos, sin Día completo).
  const jornadaUnica = sucursales.find(s => s.id === sucursalId)?.modalidadCaja === 'jornada_unica';
  const opcionesTurno = turnos.filter(t => t.id === 'dia' || t.id === 'noche');
  const turnoElegido = jornadaUnica ? 'dia_completo' : turnoFijo || (opcionesTurno.some(t => t.id === turnoId) ? turnoId : opcionesTurno[0]?.id || '');
  const nombreTurno = id => turnos.find(t => t.id === id)?.nombre || id;
  const abrir = async () => {
    if (!sucursalId || !turnoElegido) return Swal.fire('Apertura', 'Selecciona sucursal y turno.', 'warning');
    const efectivoInicial = efectivo === '' ? '0' : efectivo;
    if (!/^\d+(?:\.\d{1,2})?$/.test(efectivoInicial)) return Swal.fire('Efectivo inicial', 'Ingresa un monto válido.', 'warning');
    if (!fecha || fecha > maxFecha) return Swal.fire('Fecha', soloAnterior ? 'Elige una fecha anterior a hoy.' : 'La fecha no puede ser futura.', 'warning');
    setBusy(true);
    try {
      const a = await abrirTurno({ sucursalId, turnoId: turnoElegido, efectivoInicial, fecha });
      setActivas(v => [a, ...v]);
      navigate(`/Finanzas/RegistrarCierre?apertura=${encodeURIComponent(a.id)}`);
    } catch (e) {
      if (e?.status === 409) { setActivas(await listAperturasActivas()); Swal.fire('No se puede abrir', e.message || 'Ese turno ya tiene cuadre.', 'info'); }
      else Swal.fire('Error', e.message || 'No se pudo abrir el turno.', 'error');
    } finally { setBusy(false); }
  };

  if (!me.loaded || !loaded) return <div className="apertura-page">Cargando apertura…</div>;
  return <main className="apertura-page"><section className="apertura-card">
    <h1>Caja</h1>
    <p>{soloAnterior ? 'Elige el día que quedó sin cuadre y abre su caja.' : 'Abre la caja para comenzar el turno.'}</p>
    {!sucursalId && <p role="alert">Sin sucursal asignada.</p>}
    {!existente && <label>Fecha<input type="date" value={fecha} max={maxFecha} onChange={e => setFecha(e.target.value)} />
      {fecha < hoy && <small className="apertura-nota">Registrarás el cuadre de un día anterior. Solo es posible si ese turno aún no tiene cuadre.</small>}</label>}
    {!existente && (jornadaUnica
      ? <p className="apertura-nota">Turno: Día completo (una sola apertura y cierre durante todo el día).</p>
      : turnoFijo ? <p className="apertura-nota">Turno: {nombreTurno(turnoFijo)}</p>
      : <label>Turno<select value={turnoElegido} onChange={e => setTurnoId(e.target.value)}>{opcionesTurno.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}</select></label>)}
    {existente ? <div className="apertura-existente">
      <strong>Turno abierto</strong>
      <span>Fecha: {String(existente.fecha).split('-').reverse().join('/')}</span>
      <span>Turno: {nombreTurno(existente.turnoId)}</span>
      <span>Cajero: {existente.username}</span>
      <span>Apertura: {toMoney(existente.efectivoInicial)}</span>
      <span>Hora: {formatDateTime(existente.inicioAt)}</span>
      <button type="button" onClick={() => navigate(`/Finanzas/RegistrarCierre?apertura=${encodeURIComponent(existente.id)}`)}>Ir a Caja</button>
    </div> : <>
      <label>Efectivo inicial <span className="apertura-money"><span>Q</span><MoneyInput prefijo={false} value={efectivo} onChange={setEfectivo} /></span></label>
      <button type="button" onClick={abrir} disabled={busy || !visibles.length || !turnos.length}>{busy ? 'Abriendo…' : 'Abrir caja'}</button>
    </>}
  </section></main>;
}
